/* eslint-disable no-console */
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { IUser } from '@core/interfaces/user.interface';
import { AuthService } from '@core/services/auth.service';
import { PermissionService } from '@core/services/permission.service';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

export interface IPermissionGuardData {
  permissions?: string | string[];
  requireAll?: boolean; // If true, requires ALL permissions. If false, requires ANY permission
  roles?: string | string[];
  requireAllRoles?: boolean; // If true, requires ALL roles. If false, requires ANY role
  /**
   * Where to redirect if permission check fails. Supports:
   * - string (e.g., '/dashboard')
   * - string with tokens (e.g., '/creators/:id', '/creators/:creatorId') which are replaced from route params and user fields
   * - string[] commands for Router.navigate
   * - function to compute redirect target
   */
  redirectTo?:
    | string
    | string[]
    | ((context: { route: ActivatedRouteSnapshot; user: IUser | null }) => string | string[]);
}

/**
 * Creates permission check observables based on the provided permissions
 */
function createPermissionChecks(
  permissions: string | string[],
  requireAll: boolean,
  permissionService: PermissionService,
): Observable<boolean> {
  const permissionArray = Array.isArray(permissions) ? permissions : [permissions];

  if (requireAll) {
    return permissionService.hasAllPermissions$(permissionArray);
  } else {
    return permissionService.hasAnyPermission$(permissionArray);
  }
}

/**
 * Creates role check observables based on the provided roles
 */
function createRoleChecks(
  roles: string | string[],
  requireAllRoles: boolean,
  permissionService: PermissionService,
): Observable<boolean> {
  const roleArray = Array.isArray(roles) ? roles : [roles];

  if (requireAllRoles) {
    return permissionService
      .getCurrentUserRoles$()
      .pipe(map((userRoles) => roleArray.every((role) => userRoles.includes(role))));
  } else {
    return permissionService.hasAnyRole$(roleArray);
  }
}

/**
 * Combines and executes all permission and role checks
 */
function executePermissionChecks(
  data: IPermissionGuardData,
  permissionService: PermissionService,
): Observable<boolean> {
  const { permissions, requireAll = false, roles, requireAllRoles = false } = data;
  const checks: Observable<boolean>[] = [];

  if (permissions) {
    checks.push(createPermissionChecks(permissions, requireAll, permissionService));
  }

  if (roles) {
    checks.push(createRoleChecks(roles, requireAllRoles, permissionService));
  }

  if (checks.length === 0) {
    return of(true);
  }

  return combineLatest(checks).pipe(
    map((results) => results.every((result) => result)),
    catchError((error) => {
      console.error('Permission check error:', error);

      return of(false);
    }),
  );
}

/**
 * Handles access denial by redirecting and returning false
 */
function resolveRedirectTarget(
  redirectTo: IPermissionGuardData['redirectTo'],
  route: ActivatedRouteSnapshot,
  user: IUser | null,
): string | string[] {
  if (typeof redirectTo === 'function') {
    return redirectTo({ route, user });
  }

  if (Array.isArray(redirectTo)) {
    return redirectTo;
  }

  const target = redirectTo ?? '/dashboard';

  // This replaces tokens like :id, :creatorId from route params and user fields
  const tokens: Record<string, string | undefined> = {
    ...route.params,
    userId: user?.id ?? undefined,
  };

  return target.replace(/:([A-Za-z0-9_]+)/g, (_match: string, key: string) =>
    tokens[key] ? String(tokens[key]) : _match,
  );
}

function handleAccessDenied(
  router: Router,
  redirectTo: IPermissionGuardData['redirectTo'],
  route: ActivatedRouteSnapshot,
  user: IUser | null,
): Observable<boolean> {
  const target = resolveRedirectTarget(redirectTo, route, user);

  if (Array.isArray(target)) {
    router.navigate(target);
  } else if (target.startsWith('/')) {
    router.navigateByUrl(target);
  } else {
    router.navigate([target]);
  }

  return of(false);
}

/**
 * Validates if any permissions or roles are specified
 */
function hasPermissionRequirements(data: IPermissionGuardData): boolean {
  return !!(data.permissions?.length || data.roles?.length);
}

/**
 * Permission guard that can check single or multiple permissions/roles
 * Waits for authentication to complete before checking permissions to handle page refreshes
 *
 * Usage in routes:
 *
 * // Single permission
 * { path: 'admin', component: AdminComponent, canActivate: [permissionGuard], data: { permissions: 'admin_access' } }
 *
 * // Multiple permissions (ANY)
 * { path: 'editor', component: EditorComponent, canActivate: [permissionGuard], data: { permissions: ['edit_content', 'view_content'] } }
 *
 * // Multiple permissions (ALL)
 * { path: 'super-admin', component: SuperAdminComponent, canActivate: [permissionGuard], data: { permissions: ['admin_access', 'super_admin'], requireAll: true } }
 *
 * // Role-based access
 * { path: 'campaign-manager', component: CampaignManagerComponent, canActivate: [permissionGuard], data: { roles: 'campaign' } }
 *
 * // Combined permissions and roles
 * { path: 'special', component: SpecialComponent, canActivate: [permissionGuard], data: { permissions: 'special_access', roles: ['admin', 'campaign'] } }
 */
export const permissionGuard: CanActivateFn = (route): Observable<boolean> => {
  const permissionService = inject(PermissionService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const data = route.data as IPermissionGuardData;
  const { redirectTo = '/dashboard' } = data;

  if (!hasPermissionRequirements(data)) {
    return of(true);
  }

  // Wait for authentication to complete, then check permissions
  return authService.waitForInitialization().pipe(
    switchMap(() => combineLatest([authService.currentFirebaseUser$, authService.currentDbUser$])),
    take(1),
    switchMap(([firebaseUser, dbUser]) => {
      // If no Firebase user after initialization, user is not logged in
      // Allow access to prevent redirect loop - authGuard will handle redirecting to login
      if (!firebaseUser) {
        return of(true);
      }

      // If Firebase user exists but DB user is not loaded, deny access (will redirect)
      // This handles the case where user is logged in but DB user fetch failed or is still loading
      if (!dbUser) {
        return handleAccessDenied(router, redirectTo, route, null);
      }

      // User is logged in and DB user is loaded - check permissions
      return executePermissionChecks(data, permissionService);
    }),
    map((hasAccess) => {
      if (!hasAccess) {
        const target = resolveRedirectTarget(redirectTo, route, AuthService.currentDbUser);

        if (Array.isArray(target)) {
          router.navigate(target);
        } else if (target.startsWith('/')) {
          router.navigateByUrl(target);
        } else {
          router.navigate([target]);
        }
      }

      return hasAccess;
    }),
    catchError((error) => {
      console.error('Permission guard error:', error);

      return handleAccessDenied(router, redirectTo, route, AuthService.currentDbUser ?? null);
    }),
  );
};
